// src/pages/admin/AdminLayout.tsx
import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';

const menuItems = [
  { path: '/admin/dashboard', icon: '📊', label: 'ダッシュボード' },
  // ---- アフィリエイター管理 ----
  { path: '/admin/affiliate-registrations', icon: '📝', label: '登録申請審査', divider: true, sectionLabel: 'アフィリエイター管理' },
  { path: '/admin/affiliates', icon: '👥', label: '紹介者管理' },
  { path: '/admin/permissions', icon: '🔐', label: '紹介権限管理' },
  { path: '/admin/commissions', icon: '💰', label: '報酬管理' },
  // ---- 商品・売上管理 ----
  { path: '/admin/products', icon: '📦', label: '商品管理', divider: true, sectionLabel: '商品・売上管理' },
  { path: '/admin/purchases', icon: '🛒', label: '購入管理' },
  { path: '/admin/clicks', icon: '📈', label: 'クリック管理' },
  // ---- パートナー管理 ----
  { path: '/admin/roles', icon: '🤝', label: 'パートナー登録', divider: true, sectionLabel: 'パートナー管理' },
  { path: '/admin/approvals', icon: '✅', label: '申請審査' },
  // ---- その他 ----
  { path: '/admin/campaigns', icon: '🎯', label: 'キャンペーン', divider: true, sectionLabel: 'その他' },
  { path: '/admin/announcements', icon: '📢', label: 'お知らせ管理' },
  { path: '/admin/csv', icon: '📥', label: 'CSV出力' },
];

export function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    // 管理者認証チェック
    const token = sessionStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
    }
  }, [navigate]);

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* サイドバー */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300
        lg:static lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="h-full flex flex-col">
          {/* ロゴ */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">A</div>
              <div>
                <p className="font-bold text-gray-900 text-sm">管理者画面</p>
                <p className="text-xs text-gray-500">Affiliate Manager</p>
              </div>
            </div>
          </div>

          {/* メニュー */}
          <nav className="flex-1 overflow-y-auto py-4">
            <ul className="space-y-1 px-3">
              {menuItems.map(item => (
                <li key={item.path}>
                  {item.divider && (
                    <div className="my-2 border-t border-gray-100 pt-2">
                      <p className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">{(item as any).sectionLabel || ''}</p>
                    </div>
                  )}
                  <Link
                    to={item.path}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors duration-150 ${
                      location.pathname === item.path
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <span className="text-base">{item.icon}</span>
                    <span>{item.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* ログアウト */}
          <div className="p-4 border-t border-gray-200">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-xl transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              ログアウト
            </button>
          </div>
        </div>
      </aside>

      {/* オーバーレイ */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* メインコンテンツ */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* ヘッダー */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="ml-auto flex items-center gap-3">
              <span className="text-sm text-gray-500">管理者ページ</span>
              <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>
        </header>

        {/* コンテンツ */}
        <main className="flex-1 overflow-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
